import React, { useEffect, useState } from "react";
import { Hospital } from "lucide-react";

import { API_HOSPITAL_LIST } from "../../config/Endpoints";
import { getApi } from "../../utils/api";
import { formatCalendarDateDisplay } from "../../utils/helpers";
import { getAuthToken } from "../../utils/Storage";
import styles from "./profile-insurance-details.module.css";

function displayDate(value) {
  return value ? formatCalendarDateDisplay(value) : "-";
}

function displayGender(value) {
  const normalized = String(value ?? "").trim().toUpperCase();
  const genderLabels = {
    M: "Male",
    MALE: "Male",
    F: "Female",
    FEMALE: "Female",
    O: "Others",
    OTHER: "Others",
    OTHERS: "Others",
  };
  return genderLabels[normalized] || value || "-";
}

const ProfileInsuranceDetails = ({ userData, insuranceDetails }) => {
  const [hospitalsByCity, setHospitalsByCity] = useState({});
  const [hospitalsLoading, setHospitalsLoading] = useState(true);
  const [hospitalsError, setHospitalsError] = useState("");

  const family = insuranceDetails?.familyInsuranceDetails ?? {};
  const spouse = family.spouseInsurance ?? {};
  const child = family.childInsurance ?? {};
  const maritalStatus =
    insuranceDetails?.maritalStatus ?? userData?.personalDetail?.maritalStatus;
  const isMarried =
    String(maritalStatus ?? "").trim().toLowerCase() === "married";
  const hasChildUnder18 = Boolean(insuranceDetails?.childStatus);

  useEffect(() => {
    let active = true;
    setHospitalsLoading(true);
    setHospitalsError("");

    getApi(API_HOSPITAL_LIST, getAuthToken("accessToken"))
      .then((response) => {
        if (active) {
          setHospitalsByCity(response?.data?.data ?? response?.data ?? {});
        }
      })
      .catch(() => {
        if (active) setHospitalsError("Unable to load network hospitals.");
      })
      .finally(() => {
        if (active) setHospitalsLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  const cards = [
    { title: "Self", data: insuranceDetails },
    ...(isMarried && (
      spouse.name ||
      family.spouseName ||
      spouse.dob ||
      family.spouseDob ||
      spouse.insuranceNumber ||
      family.spouseInsuranceNumber
    )
      ? [{ title: "Spouse", data: {
          name: spouse.name ?? family.spouseName,
          dob: spouse.dob ?? family.spouseDob,
          insuranceNumber: spouse.insuranceNumber ?? family.spouseInsuranceNumber,
          insuranceFrom: spouse.insuranceFrom ?? family.spouseInsuranceFrom,
          insuranceTo: spouse.insuranceTo ?? family.spouseInsuranceTo,
        } }]
      : []),
    ...(hasChildUnder18 && (
      child.name ||
      family.childName ||
      child.dob ||
      family.childDob ||
      child.gender ||
      family.childGender ||
      child.insuranceNumber ||
      family.childInsuranceNumber
    )
      ? [{ title: "Child", data: {
          name: child.name ?? family.childName,
          dob: child.dob ?? family.childDob,
          gender: child.gender ?? family.childGender,
          insuranceNumber: child.insuranceNumber ?? family.childInsuranceNumber,
          insuranceFrom: child.insuranceFrom ?? family.childInsuranceFrom,
          insuranceTo: child.insuranceTo ?? family.childInsuranceTo,
        } }]
      : []),
  ];

  return (
    <div className={styles.page}>
      <section className={styles.hospitalSection}>
        <div className={styles.hospitalCopy}>
          <span className={styles.hospitalIcon}><Hospital size={20} /></span>
          <div>
            <h3 className="heading-xs">Network Hospitals</h3>
            <p>View hospitals covered under this insurance plan.</p>
          </div>
        </div>
        <div className={`dropdown ${styles.hospitalDropdown}`}>
          <button className={`${styles.hospitalTrigger} dropdown-toggle`} type="button" data-bs-toggle="dropdown">
            View covered hospitals
          </button>
          <ul className={`${styles.hospitalMenu} dropdown-menu dropdown-menu-end`}>
            <li className={styles.menuTitle}>Hospitals covered under this plan</li>
            {hospitalsLoading ? <li className={styles.hospitalMessage}>Loading hospitals...</li> : null}
            {hospitalsError ? <li className={styles.hospitalMessage}>{hospitalsError}</li> : null}
            {!hospitalsLoading && !hospitalsError
              ? Object.entries(hospitalsByCity).map(([city, hospitals]) => (
                  <li className={styles.cityGroup} key={city}>
                    <h4>{city}</h4>
                    <ul>
                      {(Array.isArray(hospitals) ? hospitals : []).map((hospital) => (
                        <li key={`${city}-${hospital}`} className={styles.hospitalItem}>
                          <Hospital size={16} />
                          <span>{hospital}</span>
                        </li>
                      ))}
                    </ul>
                  </li>
                ))
              : null}
          </ul>
        </div>
      </section>

      <div className={styles.insuranceCards}>
        {cards.map(({ title, data: card }) => (
          <section className={styles.insuranceCard} key={title}>
            <p className="heading-xs text-accent mb-0">{title}</p>
            <dl>
              {title !== "Self" ? (
                <div><dt>{title} Name</dt><dd>{card?.name || "-"}</dd></div>
              ) : null}
              {title !== "Self" ? (
                <div><dt>Date of Birth</dt><dd>{displayDate(card?.dob)}</dd></div>
              ) : null}
              {title === "Child" ? (
                <div><dt>Gender</dt><dd>{displayGender(card?.gender)}</dd></div>
              ) : null}
              <div><dt>Insurance Number</dt><dd>{card?.insuranceNumber || "-"}</dd></div>
              <div><dt>Valid From</dt><dd>{displayDate(card?.insuranceFrom)}</dd></div>
              <div><dt>Valid Till</dt><dd>{displayDate(card?.insuranceTo)}</dd></div>
            </dl>
          </section>
        ))}
      </div>
    </div>
  );
};

export default ProfileInsuranceDetails;
